import React, {useEffect, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import PhoneVerificationView, {
  RESEND_COOLDOWN_SECONDS,
} from '../../components/PhoneOrEmailVerification/PhoneVerificationView';
import RewardsCard from '../../components/rewards/RewardsCard';
import RewardsLink from '../../components/rewards/RewardsLink';
import StaffStorePicker from '../../components/StaffStorePicker';
import StoreItem from '../../components/StoreItem';
import Text from '../../components/themed/Text';
import Otp from '../../components/ui-controls/form/Otp';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {layout, otp, resolveWeight, space} from '../../design-system/tokens';
import {AppRoute} from '../../models/AppRoute';
import {Shop} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {VerifyPhoneStaffFailureReason} from '../../transport/types';
import {formatMobileForDisplay} from '../../utils/phoneNumber';
import {classifyStaffConflict, isStaffActivationBlocked} from '../../utils/staffActivation';
import {OtpVerificationFormData, otpVerificationSchema} from '../account/schemas/authSchemas';
import {useWideLayout} from '../account/signUp/SignUpPageLayout';

import {AccountStatus} from './types';

// SHORTCUT: the narrow branches below keep their spacing literals (16, 8 and 4
// margins, the 8 row gap) so mobile renders unchanged; the wide branches use
// layout.desktop and space. Ceiling: this screen's narrow column. Upgrade
// path: layout.mobile under DIS-1735 with a mobile screenshot sign-off.
// Desktop card OTP boxes (536:4581), stable so Otp does not re-render per pass.
const otpBoxSize = {width: otp.boxWidth, height: otp.boxHeight};

// Screen-reader-only box: in the layout tree but drawn as nothing visible.
const visuallyHidden = {
  position: 'absolute' as const,
  width: 1,
  height: 1,
  overflow: 'hidden' as const,
};

const RESEND_ERROR_MESSAGE = 'Could not resend code. Try again.';

// DIS-1673: fixed AC copy. `entered` is the six digits the user submitted,
// captured before the grid is cleared, so the code can be compared against the
// SMS without retyping it. The error Text below is an accessibilityRole="alert"
// live region, so these digits are also spoken by VoiceOver/TalkBack; the
// message is cleared the moment a new entry starts (see the effect below), so
// it never names a code the user has moved on from.
const wrongCodeMessage = (entered: string) =>
  `Incorrect code (${entered}). Check your text and re-enter.`;

// DIS-1190: surface specific phase-1 failure reasons inline. code_expired
// nudges the user to resend; max_attempts forces a fresh SMS; upstream/
// service hint at backend availability vs user error.
const getStaffVerifyErrorMessage = (
  reason: VerifyPhoneStaffFailureReason,
  entered: string,
): string => {
  switch (reason) {
    case 'code_invalid':
      return wrongCodeMessage(entered);
    case 'code_expired':
      return "Code expired. Tap 'Resend code' to get a new one.";
    case 'max_attempts':
      return "Too many attempts. Tap 'Resend code' to get a new one.";
    case 'phone_invalid':
      return 'That phone number looks invalid. Please go back and re-enter it.';
    case 'upstream':
    case 'service':
      return 'Service temporarily unavailable. Please try again shortly.';
    case 'network':
      return 'Connection issue. Please check your network and try again.';
    case 'unknown':
    default:
      return 'Something went wrong. Please try again.';
  }
};

const RewardsVerificationScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_VERIFICATION>) => {
    const {rewardsStore, userStore} = useStores();

    const [resending, setResending] = useState(false);
    const [codeResent, setCodeResent] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendError, setResendError] = useState<string | null>(null);
    // DIS-1308: change-store stays inline on this screen — navigating to
    // REWARDS_STAFF_STORE_SELECTION fired phase 3 (signUpStaff) before phase 1
    // had issued a sessionToken, yielding a 400 "sessionToken is required".
    const [isChangingStore, setIsChangingStore] = useState(false);
    // DIS-1673: the failure message CANNOT live in RHF's errors.verification.
    // Clearing the grid writes '' to Otp, which echoes onChangeText('') back
    // into field.onChange (Otp.tsx:114-117 + :121-128), re-running the resolver
    // and replacing any setError message with the zod length error. Screen
    // state is the only channel the echo cannot clobber.
    const [attemptError, setAttemptError] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(false);
    // Set synchronously before the first await. formState.isSubmitting updates
    // a render too late to stop a same-tick second press of VERIFY or Enter.
    const submitOnceRef = useRef(false);
    // The in-flight guard alone only stops a CONCURRENT second entry. onEnter
    // fired after the first attempt settles would resubmit the same digits, so
    // also remember what was last sent. Cleared by failAttempt, which is what
    // lets the user retry the very same code after a rejection.
    const lastSubmittedRef = useRef<string | null>(null);
    const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const mountedRef = useRef(true);
    // DIS-1135 bug_006: invalidate in-flight resend on blur (mountedRef
    // doesn't flip on blur with detachInactiveScreens, so a late resolve
    // would otherwise restart the cooldown / show "CODE RE-SENT" banner
    // when the user returns).
    const resendReqIdRef = useRef(0);
    const phoneNumber = rewardsStore.phoneNumber || '';
    const {flowType, returnReason} = route.params || {};

    // DIS-1185: staff recovery — staff signUp 400/500 sent the user back here
    // with returnReason set; staffStoreId + staffStore preserved on rewardsStore.
    const isStaffRecovery =
      rewardsStore.isStaffSignup &&
      returnReason === 'verification-expired' &&
      !!rewardsStore.staffStoreId &&
      !!rewardsStore.staffStore;

    // DIS-1236: staff full-signup (SIGNUP_LITE) already picked their store
    // on SignUp.tsx. Phase 3 can fire directly after phase 1 — no need to
    // visit REWARDS_STAFF_STORE_SELECTION. Existing-user staff flows
    // (NO_LITE_SIGNUP / ACTIVATE_REWARDS) skip SignUp.tsx so staffStoreId is
    // empty and the store-selection screen is still required for them.
    // Gate on both id AND full Shop: rewardsInflightPersist persists only
    // staffStoreId, so after a hard refresh staffStore is null. Falling
    // through to REWARDS_STAFF_STORE_SELECTION lets the user re-pick (their
    // phase-1 sessionToken is still valid for the 5min TTL) rather than
    // short-circuiting with no "YOUR SELECTED STORE" affordance.
    const isStaffNewSignupWithStore =
      rewardsStore.isStaffSignup &&
      flowType === AccountStatus.SIGNUP_LITE &&
      !!rewardsStore.staffStoreId &&
      !!rewardsStore.staffStore;

    const {control, handleSubmit, setValue, watch, reset} = useForm<OtpVerificationFormData>({
      resolver: zodResolver(otpVerificationSchema),
      defaultValues: {verification: ''},
      mode: 'onTouched',
    });

    // DIS-474: blur cleanup. Reset OTP form/cooldown/errors. DIS-1190
    // staff path is workflow-keyed by phone server-side; no client buffer.
    useFocusEffect(
      React.useCallback(() => {
        return () => {
          setResending(false);
          setCodeResent(false);
          setResendCooldown(0);
          setResendError(null);
          setIsChangingStore(false);
          setAttemptError(null);
          setIsVerifying(false);
          submitOnceRef.current = false;
          lastSubmittedRef.current = null;
          reset({verification: ''});
          if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
          }
          ++resendReqIdRef.current;
        };
      }, [reset]),
    );

    // DIS-1185: defensive — empty phoneNumber (e.g. page refresh wiped MobX) → SignIn.
    useFocusEffect(
      React.useCallback(() => {
        if (!rewardsStore.phoneNumber) {
          navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
        }
      }, [navigation, rewardsStore.phoneNumber, rewardsStore.signInRouteParams]),
    );

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (cooldownIntervalRef.current) {
          clearInterval(cooldownIntervalRef.current);
        }
      };
    }, []);

    const startCooldown = () => {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      cooldownIntervalRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };

    // DIS-1673: every retryable failure clears the grid. The message, not the
    // empty grid, carries the reason. The user retypes the code and presses
    // VERIFY again, which is disabled below 6 digits (DIS-1730).
    const failAttempt = (message: string) => {
      setAttemptError(message);
      lastSubmittedRef.current = null;
      setValue('verification', '');
    };

    const verificationValue = watch('verification');

    // DIS-1824: no auto-submit. The code is sent only by VERIFY or Enter, on
    // every mobile verification screen. This replaces the DIS-1673 auto-submit.

    // DIS-1673: drop the failure message as soon as a new entry begins. The
    // message names the code that was rejected, so leaving it up while the user
    // retypes would caption fresh digits with a stale code. failAttempt clears
    // the grid to '', so the first digit of the retry is the transition here.
    useEffect(() => {
      if (verificationValue?.length) {
        setAttemptError(null);
      }
    }, [verificationValue]);

    const onSubmit = async (values: OtpVerificationFormData) => {
      // VERIFY and onEnter (on the Otp element) both call handleSubmit(onSubmit).
      // A second press or a stray Enter would otherwise burn a second Twilio
      // attempt and can trip max_attempts on the staged-staff path.
      if (submitOnceRef.current) return;
      if (lastSubmittedRef.current === values.verification) return;
      submitOnceRef.current = true;
      lastSubmittedRef.current = values.verification;
      setIsVerifying(true);
      setAttemptError(null);
      try {
        await runVerification(values);
      } finally {
        submitOnceRef.current = false;
        setIsVerifying(false);
      }
    };

    const runVerification = async (values: OtpVerificationFormData) => {
      // DIS-1190: staff path uses the two-phase verify-lite flow. Phase 1
      // verifies the SMS code with the backend (which parks a Temporal
      // workflow keyed on phone). Phase 3 runs at StaffStoreSelectionScreen
      // (or here on staff-recovery, since the store was already chosen).
      // DIS-1384 (R7): broadened from isStaffSignup to usesStagedVerification so
      // LMG-staff (isStaffSignup=false, isLmgStaffFlow=true) rides the same staged
      // verify rails as store-staff.
      if (rewardsStore.usesStagedVerification) {
        const verifyResult = await rewardsStore.verifyStaffPhone(values.verification);
        if (!verifyResult.ok) {
          // DIS-1673: every reason keeps its own typed message and still
          // clears the grid for the retry.
          failAttempt(getStaffVerifyErrorMessage(verifyResult.reason, values.verification));
          return;
        }

        // DIS-1384 (R7): LMG staff always complete on the dedicated department/
        // state selection screen — route there after OTP, BEFORE the store-staff
        // inline-complete and store-selection branches below (isStaffRecovery and
        // isStaffNewSignupWithStore are both false for LMG, so control would
        // otherwise fall through to the store picker at the end of this block).
        if (rewardsStore.isLmgStaffFlow) {
          navigation.navigate(AppRoute.REWARDS_LMG_STAFF_SELECTION, {
            flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
          });
          return;
        }

        // DIS-1185 / DIS-1236: store already selected (recovery OR full-signup
        // SIGNUP_LITE). Skip REWARDS_STAFF_STORE_SELECTION and complete phase 3
        // directly so the user doesn't re-pick the store they already chose.
        if (isStaffRecovery || isStaffNewSignupWithStore) {
          const completeResult = await rewardsStore.signUpStaff(
            flowType || AccountStatus.NO_LITE_SIGNUP,
          );
          if (completeResult.ok) {
            // DIS-1329: staff promotion blocked (Zen↔Zoho identity mismatch) —
            // block the staff flow and divert to the terminal screen instead of
            // Success.
            if (isStaffActivationBlocked(completeResult.data)) {
              navigation.navigate(AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED, {
                conflictKind: classifyStaffConflict(completeResult.data.staffActivation?.reason),
              });
              return;
            }
            navigation.navigate(AppRoute.REWARDS_SUCCESS);
            return;
          }
          if (completeResult.reason === 'account_not_found') {
            // DIS-1328: 422 — no CRM contact for this phone. Terminal, no retry.
            navigation.navigate(AppRoute.REWARDS_ACCOUNT_NOT_FOUND);
            return;
          }
          if (completeResult.reason === 'session_expired') {
            // Workflow timed out between phases — start over from phase 1.
            failAttempt("Verification expired. Tap 'Resend code' to get a new one.");
            return;
          }
          if (completeResult.reason === 'invalid_store_id') {
            // Rare but real: store can be deactivated/delisted between SignUp
            // selection and phase 3 (Airtable sync lag, admin changes). Clear
            // the stale selection and route to the picker so the user can
            // choose another.
            rewardsStore.setStaffStoreId(null);
            rewardsStore.setStaffStore(null);
            navigation.navigate(AppRoute.REWARDS_STAFF_STORE_SELECTION, {
              flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
            });
            return;
          }
          failAttempt('Something went wrong. Please try again.');
          return;
        }

        navigation.navigate(AppRoute.REWARDS_STAFF_STORE_SELECTION, {
          flowType: flowType || AccountStatus.NO_LITE_SIGNUP,
        });
        return;
      }

      const shouldLogMeInThenActivate =
        flowType === AccountStatus.ACTIVATE_REWARDS && !rewardsStore.isStaffSignup;

      const signUpResponse = await rewardsStore.signUp(
        values.verification,
        flowType || AccountStatus.NO_LITE_SIGNUP,
      );
      if (signUpResponse.ok) {
        if (shouldLogMeInThenActivate) {
          userStore.setPostLoginReturnToScreen(AppRoute.ROOT_REWARDS_FLOW, {
            screen: AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS,
            params: {activateFlow: 'logMeIn'},
          });

          navigation.navigate(
            AppRoute.ROOT as never,
            {
              screen: AppRoute.MAIN,
              params: {screen: AppRoute.ACCOUNT_NOT_LOGGED_IN},
            } as never,
          );
        } else {
          navigation.navigate(AppRoute.REWARDS_SUCCESS);
        }
      } else if (signUpResponse.reason === 'account_not_found') {
        // DIS-1328: 422 — no CRM contact for this phone. Terminal, no retry.
        navigation.navigate(AppRoute.REWARDS_ACCOUNT_NOT_FOUND);
      } else {
        failAttempt(getStaffVerifyErrorMessage(signUpResponse.reason, values.verification));
      }
    };

    const resendCode = async () => {
      if (!rewardsStore.phoneNumber) {
        navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);
        return;
      }
      if (resending || resendCooldown > 0) return;
      const myReqId = ++resendReqIdRef.current;
      reset({verification: ''});
      setResendError(null);
      setResending(true);

      try {
        const result = await apiService.rewardsVerifyExistingUserPhoneNumber({
          phoneNumber: rewardsStore.phoneNumber,
        });
        if (!mountedRef.current || myReqId !== resendReqIdRef.current) return;

        const success = result?.status === 200;
        setCodeResent(success);
        if (success) {
          startCooldown();
        } else {
          setResendError(RESEND_ERROR_MESSAGE);
        }
      } catch (_e) {
        if (!mountedRef.current || myReqId !== resendReqIdRef.current) return;
        setCodeResent(false);
        setResendError(RESEND_ERROR_MESSAGE);
      } finally {
        if (mountedRef.current && myReqId === resendReqIdRef.current) {
          setResending(false);
        }
      }
    };

    const dayName = new Date().toLocaleString('en-AU', {weekday: 'long'}).toLowerCase();

    const backToSignIn = () =>
      navigation.navigate(AppRoute.REWARDS_SIGN_IN, rewardsStore.signInRouteParams);

    const isNewMember = flowType === AccountStatus.SIGNUP_LITE;
    const {isDesktopCard: isWide, onLayout} = useWideLayout();

    // DIS-1673: polite live region announcing the in-flight check. DIS-1860:
    // VERIFY's own spinner is the only visible loader, so this row is visually
    // hidden (a 1 x 1 clipped box, not opacity 0, which native screen readers
    // skip) and stays for the screen-reader announcement only.
    const verifyingIndicator = isVerifying ? (
      <View
        style={visuallyHidden}
        accessibilityRole="text"
        accessibilityLiveRegion="polite"
        testID="verifying-indicator">
        <Text variant="body2" color="textMuted">
          Verifying…
        </Text>
      </View>
    ) : null;

    const verificationView = (
      <PhoneVerificationView
        title={isNewMember ? 'VERIFY YOUR MOBILE' : 'WELCOME BACK'}
        // DIS-1860: two centred lines, the number on its own line, for new
        // and returning members alike.
        intro={`Enter the 6-digit code we sent to\n${formatMobileForDisplay(phoneNumber)}`}
        notice={codeResent ? 'We have re-sent your code to your mobile.' : undefined}
        otp={
          <Controller
            control={control}
            name="verification"
            render={({field: {onChange, onBlur, value}}) => (
              <Otp
                value={value}
                numInputs={6}
                onChangeText={onChange}
                onEnter={handleSubmit(onSubmit)}
                onBlur={onBlur}
                error={!!attemptError}
                // Desktop card: Figma 536:4581's fixed 49 x 52 boxes.
                boxSize={isWide ? otpBoxSize : undefined}
              />
            )}
          />
        }
        error={attemptError ?? undefined}
        // DIS-1730: VERIFY is back (board 6.png). DIS-1824: it is the only
        // way to send the code besides Enter. The guard in onSubmit stops
        // it sending a code twice.
        verifyDisabled={verificationValue?.length !== 6 || isVerifying}
        verifying={isVerifying}
        onVerify={handleSubmit(onSubmit)}
        resend={resending ? 'sending' : resendCooldown > 0 ? resendCooldown : 'ready'}
        resendError={resendError ?? undefined}
        onResend={resendCode}
        // DIS-1343: return to the original sign-in entry point with
        // staff/signUpCode preserved, instead of popping to a mid-flow
        // screen. Also fixes the dead button after a deep-link/refresh
        // where canGoBack() is false.
        onChangeNumber={backToSignIn}
        onBack={backToSignIn}
        footer={verifyingIndicator}
        // Desktop card (536:4455), opted in from this screen only.
        centredOnLayout={isWide ? onLayout : undefined}>
        {returnReason === 'verification-expired' ? (
          <RewardsCard
            variant="warning"
            padding="s3"
            // Wide: the card's section gap is above it; a group gap to the OTP row.
            style={isWide ? {marginBottom: layout.desktop.stackGroup} : {marginTop: 16}}
            accessibilityLabel="The code you entered was incorrect. Re-enter the code below or request a new one."
            testID="verification-expired-banner">
            <Text
              variant="body2"
              color="textWarning"
              // DIS-1730: was fontWeight '600', which Android renders Regular.
              // Wide: centred, like every other line in the desktop card.
              style={
                isWide
                  ? [resolveWeight('semiBold'), {textAlign: 'center'}]
                  : resolveWeight('semiBold')
              }
              accessibilityRole="alert"
              accessibilityLiveRegion="polite">
              The code you entered was incorrect.
            </Text>
            <Text
              variant="body2"
              color="textWarning"
              style={isWide ? {marginTop: space.s4, textAlign: 'center'} : {marginTop: 4}}>
              Re-enter the code below, or tap &quot;Resend code&quot; to request a new one.
            </Text>
          </RewardsCard>
        ) : null}

        {(isStaffRecovery || isStaffNewSignupWithStore) && rewardsStore.staffStore ? (
          <View
            style={isWide ? {marginBottom: layout.desktop.stackGroup} : {marginTop: 16}}
            testID="staff-recovery-store-card">
            <Text variant="subheading">YOUR SELECTED STORE</Text>
            <RewardsCard variant="subtle" padding="s3">
              <StoreItem item={rewardsStore.staffStore} dayName={dayName} />
              <RewardsLink
                onPress={() => setIsChangingStore(true)}
                align="flex-start"
                accessibilityLabel="Change selected store"
                testID="change-store-button"
                style={{marginTop: 8}}>
                Change Store
              </RewardsLink>
            </RewardsCard>
            {isChangingStore ? (
              <StaffStorePicker
                mode="compact"
                selectedStore={null}
                hideTrigger
                autoOpenModal
                onModalCancel={() => setIsChangingStore(false)}
                onStoreSelected={(shop: Shop) => {
                  rewardsStore.setStaffStoreId(shop.store_number);
                  rewardsStore.setStaffStore(shop);
                  setIsChangingStore(false);
                }}
                testID="change-store-picker"
              />
            ) : null}
          </View>
        ) : null}
      </PhoneVerificationView>
    );

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flexGrow: 1}}
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}>
        {isWide ? (
          verificationView
        ) : (
          <ContentView parentStyle={{paddingBottom: 16}} style={{flex: 1}} onLayout={onLayout}>
            {verificationView}
          </ContentView>
        )}
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsVerificationScreen;
